/**
 * Calendar utility functions for the linear yearly calendar
 */

/**
 * Get the number of days in a month
 * @param {number} year - The year
 * @param {number} month - The month (0-11)
 * @returns {number} Number of days in the month
 */
export function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Get the day of week for the first day of a month
 * @param {number} year - The year
 * @param {number} month - The month (0-11)
 * @returns {number} Day of week (0 = Monday, 6 = Sunday)
 */
export function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay();
  // Convert from Sunday = 0 to Monday = 0
  return day === 0 ? 6 : day - 1;
}

/**
 * Get the total number of columns needed for the calendar
 * This is the maximum of (offset + days) across all months
 * @param {number} year - The year
 * @returns {number} Total columns needed
 */
export function getTotalColumns(year) {
  let maxColumns = 0;
  for (let month = 0; month < 12; month++) {
    const offset = getFirstDayOfMonth(year, month);
    const days = getDaysInMonth(year, month);
    maxColumns = Math.max(maxColumns, offset + days);
  }
  return maxColumns;
}

/** Default month names (English) - used if translations not provided */
const DEFAULT_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DEFAULT_MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

/**
 * Generate calendar data for a year
 * @param {number} year - The year
 * @param {Object} [options] - Optional configuration
 * @param {Array} [options.months] - Translated month names
 * @param {Array} [options.monthsShort] - Translated short month names
 * @returns {Array} Array of month data objects
 */
export function generateYearData(year, options = {}) {
  const months = options.months || DEFAULT_MONTHS;
  const monthsShort = options.monthsShort || DEFAULT_MONTHS_SHORT;

  const totalColumns = getTotalColumns(year);
  const today = new Date();
  const isCurrentYear = today.getFullYear() === year;

  return months.map((name, index) => {
    const offset = getFirstDayOfMonth(year, index);
    const daysInMonth = getDaysInMonth(year, index);
    
    // Create array of day cells
    const cells = [];
    
    // Add empty cells for offset
    for (let i = 0; i < offset; i++) {
      cells.push({ type: 'empty', dayOfWeek: i });
    }
    
    // Add day cells
    for (let day = 1; day <= daysInMonth; day++) {
      const dayOfWeek = (offset + day - 1) % 7;
      const isWeekend = dayOfWeek === 5 || dayOfWeek === 6; // Saturday or Sunday
      const isToday = isCurrentYear && today.getMonth() === index && today.getDate() === day;
      
      cells.push({
        type: 'day',
        day,
        dayOfWeek,
        isWeekend,
        isToday,
        date: new Date(year, index, day)
      });
    }
    
    // Add trailing empty cells to fill the row
    const trailingEmpty = totalColumns - cells.length;
    for (let i = 0; i < trailingEmpty; i++) {
      const dayOfWeek = (offset + daysInMonth + i) % 7;
      cells.push({ type: 'empty', dayOfWeek });
    }

    return {
      name,
      shortName: monthsShort[index],
      index,
      offset,
      daysInMonth,
      cells
    };
  });
}

/** Default day labels (English) - used if translations not provided */
const DEFAULT_DAYS_SHORT = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

/**
 * Generate column headers (day of week labels)
 * @param {number} totalColumns - Total number of columns
 * @param {Array} [daysShort] - Translated day labels (defaults to English)
 * @returns {Array} Array of day labels
 */
export function generateColumnHeaders(totalColumns, daysShort = DEFAULT_DAYS_SHORT) {
  const headers = [];
  
  for (let i = 0; i < totalColumns; i++) {
    const dayIndex = i % 7;
    headers.push({
      label: daysShort[dayIndex],
      dayOfWeek: dayIndex,
      isWeekend: dayIndex === 5 || dayIndex === 6
    });
  }
  
  return headers;
}
